// src/components/SubscriptionAwareReceptionistDashboard.js

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSubscription } from '../hooks/useSubscription';
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
} from '@mui/material';
import {
  Upgrade as UpgradeIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const SubscriptionAwareReceptionistDashboard = ({ children }) => {
  const { user } = useSelector((state) => state.auth);
  const {
    loading,
    error,
    checkFeatureAccess,
    getSubscriptionInfo,
    getUsageInfo,
    refreshSubscription,
  } = useSubscription();

  // Refresh subscription status when component mounts
  useEffect(() => {
    if (user?.role === 'receptionist') {
      refreshSubscription();
    }
  }, [user?.role, refreshSubscription]);

  // Show loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading subscription information...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Subscription Error</AlertTitle>
          {error}
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={refreshSubscription}
              startIcon={<InfoIcon />}
            >
              Retry
            </Button>
          </Box>
        </Alert>
        {children}
      </Box>
    );
  }

  const subscriptionInfo = getSubscriptionInfo();

  // Show subscription status banner for receptionists
  const SubscriptionBanner = () => {
    if (!subscriptionInfo) return null;

    const { isActive, isTrial, planName, daysRemaining, clinicName, message, userRole } = subscriptionInfo;

    // Show clinic assignment status
    if (message && message.includes('not associated')) {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Clinic Assignment Required</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You are not currently associated with any clinic. Please contact your administrator 
            to be assigned to a clinic to access full receptionist features.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label="Basic Access" color="warning" size="small" />
            <Chip label="Receptionist" color="info" size="small" />
          </Stack>
        </Alert>
      );
    }

    // Show subscription status for clinic-associated receptionists
    return (
      <Card elevation={1} sx={{ mb: 2, bgcolor: isActive ? 'success.50' : 'warning.50' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                {clinicName || 'Clinic'} - {planName} Plan
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip 
                  label={isActive ? 'Active' : 'Inactive'} 
                  color={isActive ? 'success' : 'error'} 
                  size="small" 
                />
                {isTrial && (
                  <Chip 
                    label={`Trial: ${daysRemaining} days left`} 
                    color="info" 
                    size="small" 
                  />
                )}
                <Chip label="Receptionist" color="primary" size="small" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Access level inherited from clinic subscription
              </Typography>
            </Box>
            
            {!isActive && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<UpgradeIcon />}
                size="small"
              >
                Contact Admin
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Feature access wrapper
  const FeatureWrapper = ({ featureName, children, fallbackMessage }) => {
    const hasAccess = checkFeatureAccess(featureName);
    
    if (!hasAccess) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          <AlertTitle>Feature Not Available</AlertTitle>
          {fallbackMessage || `This feature requires a higher subscription plan. Contact your clinic administrator for access to ${featureName}.`}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<WarningIcon />}
              size="small"
            >
              Contact Administrator
            </Button>
          </Box>
        </Alert>
      );
    }
    
    return children;
  };

  // Usage indicator component
  const UsageIndicator = ({ featureName }) => {
    const usage = getUsageInfo(featureName);
    if (!usage || usage.limit === -1) return null;

    const percentage = (usage.current_usage / usage.limit) * 100;
    const isNearLimit = percentage > 80;
    const isAtLimit = usage.is_limit_reached;

    return (
      <Alert 
        severity={isAtLimit ? 'error' : isNearLimit ? 'warning' : 'info'} 
        sx={{ mb: 1 }}
      >
        <Typography variant="body2">
          {featureName}: {usage.current_usage} / {usage.limit} used
          {isAtLimit && ' (Limit reached)'}
          {isNearLimit && !isAtLimit && ' (Near limit)'}
        </Typography>
      </Alert>
    );
  };

  // Enhanced children with subscription context
  const enhancedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        subscriptionInfo,
        checkFeatureAccess,
        getUsageInfo,
        FeatureWrapper,
        UsageIndicator,
      });
    }
    return child;
  });

  return (
    <Box>
      <SubscriptionBanner />
      {enhancedChildren}
    </Box>
  );
};

export default SubscriptionAwareReceptionistDashboard;
